import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { styled, Stack, Text } from '@hub-la/design-system';
import { useSaveAssetProgress } from '@hub-la/fe-asset';
import { useContainer } from '@hub-la/fe-container';
import { Analytics } from '@hub-la/fe-core-analytics';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Skeleton } from '@mui/material';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfjs, Document, Page } from 'react-pdf';
import '../../../domain/dtos/alert-type.js';
import '../../../domain/dtos/error-type.js';
import { Events } from '../../../domain/dtos/events.js';
import '../../../domain/dtos/main-content-type.js';
import '../../../domain/dtos/post-event-type.js';
import '../../../domain/dtos/post-status.js';
import '../../../domain/dtos/query-key.js';
import '../../../domain/dtos/remove-dialog-action.js';
import { useGetSectionProgress } from '../../hooks/use-get-section-progress.js';
import { useRealtimeSections } from '../../hooks/use-realtime-sections.js';

let _ = t => t,
  _t;
const Container = styled(Stack)(_t || (_t = _`
  .document-pdf {
    display: flex;
    align-items: center;
    justify-content: center;
    object-fit: contain;
  }

  .document-pdf-page {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .document-pdf-paginate {
    display: flex;
    overflow: auto;
    align-items: center;
    justify-content: center;
    margin: auto;
  }
`));
const MediaDocumentPDF = props => {
  const {
    previewHeight,
    url,
    assetId,
    progress,
    file,
    readonly,
    sectionId,
    postId
  } = props;
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  const {
    t
  } = useTranslation();
  const container = useContainer();
  const analytics = container.get(Analytics);
  const {
    dispatch,
    setRealtimePostProgress
  } = useRealtimeSections();
  const {
    mutateAsync: getSectionProgress
  } = useGetSectionProgress();
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(progress !== null && progress !== void 0 ? progress : 1);
  const saveAssetProgress = useSaveAssetProgress();
  const changePage = useCallback(offset => {
    setPageNumber(prevPageNumber => {
      const nextPage = (prevPageNumber || 1) + offset;
      if (prevPageNumber === 1) {
        analytics.track(Events.HUB.PROGRESS.STARTED, {
          assetId,
          type: 'application',
          userProgress: progress !== null && progress !== void 0 ? progress : 0,
          current: pageNumber + 1,
          total: numPages
        });
      } else if (numPages === nextPage) {
        analytics.track(Events.HUB.PROGRESS.ENDED, {
          assetId,
          type: 'application',
          userProgress: progress !== null && progress !== void 0 ? progress : 0,
          current: pageNumber + 1,
          total: numPages
        });
      }
      return nextPage;
    });
  }, [numPages, analytics, assetId, progress, pageNumber]);
  const setPdfProgress = useCallback((currentPage, totalPage) => {
    setRealtimePostProgress(currentPage);
    dispatch({
      type: 'update',
      sectionId,
      postId,
      currentTime: currentPage
    });
    getSectionProgress(sectionId).then(sectionProgress => dispatch({
      type: 'update',
      sectionId,
      postId,
      currentTime: currentPage,
      sectionProgress
    }));
    saveAssetProgress.mutateAsync({
      assetId,
      total: totalPage,
      currentProgress: currentPage
    }).catch(error => {
      console.error(error);
      analytics.track(Events.HUB.PROGRESS.SAVE_FAILED, {
        assetId,
        type: 'application',
        userProgress: progress !== null && progress !== void 0 ? progress : 0,
        current: currentPage,
        total: totalPage
      });
    });
  }, [analytics, assetId, progress, saveAssetProgress]);
  const previousPage = useCallback(() => {
    changePage(-1);
    setPdfProgress(pageNumber - 1, numPages);
  }, [changePage, numPages, pageNumber, setPdfProgress]);
  const nextPage = () => {
    changePage(1);
    setPdfProgress(pageNumber + 1, numPages);
  };
  const onDocumentLoadSuccess = ({
    numPages
  }) => {
    setNumPages(numPages);
    setPageNumber(progress !== null && progress !== void 0 ? progress : 1);
    !readonly && setPdfProgress(pageNumber, numPages);
  };
  const onFullscreenClick = () => {
    analytics.track(Events.HUB.POST_PDF_FULLSCREEN_CLICKED, {
      assetId
    });
    window.open(`${url}#toolbar=0&page=${pageNumber}`);
  };
  if (readonly) {
    return jsx(Container, {
      children: jsx(Document, Object.assign({
        onLoadSuccess: onDocumentLoadSuccess,
        loading: jsx(Skeleton, {
          variant: "rectangular",
          width: previewHeight,
          height: previewHeight,
          animation: "wave"
        }),
        file: url !== null && url !== void 0 ? url : file,
        error: t(`mediaLoad.error.pdf`),
        className: "document-pdf",
        renderMode: "canvas"
      }, {
        children: jsx(Page, {
          height: previewHeight,
          className: "document-pdf-page",
          pageNumber: 1,
          renderAnnotationLayer: false
        })
      }))
    });
  }
  const isForwardDisabled = pageNumber >= numPages;
  return jsxs(Container, {
    children: [jsx(Document, Object.assign({
      onLoadSuccess: onDocumentLoadSuccess,
      loading: jsx(Skeleton, {
        variant: "rectangular",
        width: previewHeight,
        height: previewHeight,
        animation: "wave"
      }),
      file: url !== null && url !== void 0 ? url : file,
      error: t(`mediaLoad.error.pdf`),
      className: "document-pdf-paginate",
      renderMode: "canvas"
    }, {
      children: jsx(Page, {
        renderAnnotationLayer: false,
        pageNumber: pageNumber,
        height: previewHeight
      })
    })), jsxs(Stack, Object.assign({
      direction: "row",
      spacing: 2,
      alignItems: "center"
    }, {
      children: [jsx(ArrowBackIosIcon, {
        "data-testid": "arrow-back",
        fontSize: "small",
        color: pageNumber <= 1 ? 'disabled' : 'inherit',
        onClick: pageNumber <= 1 ? undefined : previousPage,
        style: {
          cursor: pageNumber <= 1 ? 'auto' : 'pointer'
        }
      }), jsx(ArrowForwardIosIcon, {
        "data-testid": "arrow-forward",
        fontSize: "small",
        color: isForwardDisabled ? 'disabled' : 'inherit',
        onClick: isForwardDisabled ? undefined : nextPage,
        style: {
          cursor: isForwardDisabled ? 'auto' : 'pointer'
        }
      }), jsx(Text, Object.assign({
        variant: "body2",
        flex: 1
      }, {
        children: t('pdf.pagination', {
          pageNumber,
          numPages
        })
      })), jsx(FullscreenIcon, {
        onClick: onFullscreenClick,
        style: {
          cursor: 'pointer'
        },
        "data-testid": "fullscreen-button"
      })]
    }))]
  });
};

export { MediaDocumentPDF };
